import { logger } from './logger';
import jsPDF from 'jspdf';
import { ClinicalHistory } from '../types';
function formatDateForPdf(date: Date | string): string {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'America/Santo_Domingo'
  });
}
export async function generateClinicalHistoryPdf(history: ClinicalHistory, patientName?: string, attachedImages?: {
  name: string;
  data: string;
}[]) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;
  const colors = {
    primary: [0, 120, 150] as [number, number, number],
    dark: [30, 30, 30] as [number, number, number],
    gray: [100, 100, 100] as [number, number, number],
    lightGray: [160, 160, 160] as [number, number, number],
    bg: [245, 248, 250] as [number, number, number],
    white: [255, 255, 255] as [number, number, number],
    line: [200, 210, 215] as [number, number, number],
    accent: [0, 150, 180] as [number, number, number]
  };

  // ========== REUSABLE HEADER FUNCTION ==========
  function drawPageHeader() {
    // Top accent bar
    doc.setFillColor(...colors.primary);
    doc.rect(0, 0, pageWidth, 3, 'F');
    y = 10;

    // ── Logo Savidna embebido ──
    const logoSize = 24;
    const LOGO_DATA = "data:image/jpeg;base64,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";
    try {
      doc.addImage(LOGO_DATA, 'JPEG', margin, y - 1, logoSize, logoSize);
    } catch (_) {
      // fallback: círculo teal
      doc.setFillColor(100, 200, 200);
      doc.circle(margin + logoSize / 2, y + logoSize / 2 - 1, logoSize / 2, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255);
      doc.text('SV', margin + logoSize / 2, y + logoSize / 2 + 2, {
        align: 'center'
      });
    }
    // Title block - next to logo
    const titleX = margin + logoSize + 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(...colors.primary);
    doc.text('HISTORIA CLÍNICA', titleX, y + 6);

    // "Savidna" brand text below title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...colors.accent);
    doc.text('Savidna - Centro de Bienestar y Belleza', titleX, y + 13);

    // Date on the right
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...colors.lightGray);
    doc.text('Fecha:', pageWidth - margin - 40, y + 4);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(...colors.dark);
    doc.text(formatDateForPdf(history.date), pageWidth - margin - 40, y + 9);
    y += 26;

    // Divider
    doc.setDrawColor(...colors.primary);
    doc.setLineWidth(0.8);
    doc.line(margin, y, pageWidth - margin, y);
    y += 6;
  }
  function checkPageBreak(needed: number) {
    if (y + needed > pageHeight - 25) {
      doc.addPage();
      drawPageHeader();
      return true;
    }
    return false;
  }
  function drawSectionTitle(title: string) {
    checkPageBreak(14);
    y += 4;
    doc.setFillColor(...colors.primary);
    doc.roundedRect(margin, y, contentWidth, 9, 1.5, 1.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(...colors.white);
    doc.text(title.toUpperCase(), margin + 4, y + 6.2);
    y += 13;
    doc.setTextColor(...colors.dark);
  }
  function drawField(label: string, value: string, fullWidth = false) {
    checkPageBreak(10);
    const fieldWidth = fullWidth ? contentWidth : contentWidth / 2 - 2;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...colors.gray);
    doc.text(label, currentX, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(...colors.dark);
    const lines = doc.splitTextToSize(value || '-', fieldWidth - 2);
    doc.text(lines, currentX, y + 4.5);
    return 4.5 + lines.length * 4;
  }
  let currentX = margin;
  function drawFieldPair(label1: string, value1: string, label2: string, value2: string) {
    checkPageBreak(14);
    const halfWidth = contentWidth / 2 - 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...colors.gray);
    doc.text(label1, margin, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(...colors.dark);
    const lines1 = doc.splitTextToSize(value1 || '-', halfWidth);
    doc.text(lines1, margin, y + 4.5);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...colors.gray);
    doc.text(label2, margin + contentWidth / 2 + 2, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(...colors.dark);
    const lines2 = doc.splitTextToSize(value2 || '-', halfWidth);
    doc.text(lines2, margin + contentWidth / 2 + 2, y + 4.5);
    y += 4.5 + Math.max(lines1.length, lines2.length) * 4 + 2;
  }
  function drawTextBlock(label: string, value: string) {
    checkPageBreak(16);
    if (label) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(...colors.gray);
      doc.text(label, margin, y);
      y += 4.5;
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(...colors.dark);
    const lines = doc.splitTextToSize(value || '-', contentWidth - 4);
    for (const line of lines) {
      checkPageBreak(5);
      doc.text(line, margin + 1, y);
      y += 4.2;
    }
    y += 2;
  }

  // ========== PAGE 1: HEADER ==========
  drawPageHeader();

  // ========== PATIENT INFO BOX ==========
  doc.setFillColor(...colors.bg);
  doc.roundedRect(margin, y, contentWidth, 28, 2, 2, 'F');
  doc.setDrawColor(...colors.line);
  doc.setLineWidth(0.3);
  doc.roundedRect(margin, y, contentWidth, 28, 2, 2, 'S');
  const pb = history.personalBackground;
  const boxY = y + 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...colors.gray);
  doc.text('Paciente:', margin + 5, boxY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...colors.dark);
  doc.text(pb.fullName || patientName || '-', margin + 28, boxY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...colors.gray);
  doc.text('Edad:', margin + 5, boxY + 7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...colors.dark);
  doc.text(`${pb.age} años`, margin + 20, boxY + 7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.gray);
  doc.text('Sexo:', margin + 50, boxY + 7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...colors.dark);
  doc.text(pb.gender || '-', margin + 65, boxY + 7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.gray);
  doc.text('Teléfono:', margin + 100, boxY + 7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...colors.dark);
  doc.text(pb.phone || '-', margin + 122, boxY + 7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.gray);
  doc.text('Email:', margin + 5, boxY + 14);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...colors.dark);
  doc.text(pb.email || '-', margin + 22, boxY + 14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.gray);
  doc.text('Dirección:', margin + 100, boxY + 14);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...colors.dark);
  const addrLines = doc.splitTextToSize(pb.address || '-', 55);
  doc.text(addrLines[0] || '-', margin + 122, boxY + 14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.gray);
  doc.text('Doctor:', margin + 5, boxY + 21);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...colors.dark);
  doc.text(`Dr. ${history.doctorName}`, margin + 24, boxY + 21);
  y += 34;

  // ========== ANTECEDENTES PERSONALES ==========
  drawSectionTitle('Antecedentes Personales');
  drawFieldPair('Ocupación:', pb.occupation, 'N° de Hijos:', String(pb.numberOfChildren));
  drawFieldPair('Tipo de Parto:', pb.deliveryType, 'F. Nacimiento:', pb.birthDate || '-');
  drawFieldPair('Alcohol:', pb.alcohol, 'Tabaco:', pb.tobacco);
  drawFieldPair('Drogas:', pb.drugs, 'Infusiones:', pb.infusions);
  drawTextBlock('Motivo de la Consulta:', pb.consultationReason);

  // ========== ANTECEDENTES CLÍNICOS ==========
  const cb = history.clinicalBackground;
  drawSectionTitle('Antecedentes Clínicos');
  drawTextBlock('Enfermedades:', cb.diseases);
  drawTextBlock('Medicamentos Actuales:', cb.currentMedications);
  drawTextBlock('Enfermedades Crónicas:', cb.chronicDiseases);
  drawTextBlock('Antecedentes Quirúrgicos:', cb.surgicalHistory);
  drawFieldPair('Implantes/Dispositivos:', cb.implantsOrDevices, 'Tipo:', cb.implantType);
  drawTextBlock('Alergias:', cb.allergies);

  // ========== DIAGNÓSTICO ==========
  drawSectionTitle('Diagnóstico');
  drawTextBlock('', history.diagnosis);

  // ========== FORZAR PÁGINA 2 ==========
  doc.addPage();
  drawPageHeader();

  // ========== EXPLORACIÓN FÍSICA (PÁGINA 2) ==========
  const pe = history.physicalExam;
  if (pe && (pe.bloodPressure || pe.bloodSugar || pe.heartRate || pe.lungs || pe.eyes || pe.throat || pe.abdomen || pe.weight || pe.height || pe.temperature)) {
    drawSectionTitle('Exploración Física');
    drawFieldPair('Presión Arterial:', pe.bloodPressure, 'Azúcar en Sangre:', pe.bloodSugar ? `${pe.bloodSugar} mg/dL` : '-');
    drawFieldPair('Ritmo Cardíaco:', pe.heartRate ? `${pe.heartRate} bpm` : '-', 'Pulmones:', pe.lungs);
    drawFieldPair('Ojos:', pe.eyes, 'Garganta:', pe.throat);
    drawFieldPair('Abdomen:', pe.abdomen, 'Peso:', pe.weight ? `${pe.weight} kg` : '-');
    drawFieldPair('Altura:', pe.height ? `${pe.height} cm` : '-', 'Temperatura:', pe.temperature ? `${pe.temperature} °C` : '-');
  }

  // ========== TRATAMIENTO (PÁGINA 2) ==========
  drawSectionTitle('Tratamiento');
  drawTextBlock('', history.treatment);

  // ========== INDICACIONES MÉDICAS ==========
  drawSectionTitle('Indicaciones Médicas');
  drawTextBlock('', history.medicalIndications || '-');

  // ========== SESIONES ==========
  drawSectionTitle('Sesiones');
  if (history.sessionServices && history.sessionServices.length > 0) {
    // Per-service session numbers table
    checkPageBreak(10 + history.sessionServices.length * 8);

    // Table header
    doc.setFillColor(...colors.bg);
    doc.roundedRect(margin, y, contentWidth, 7, 1, 1, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...colors.gray);
    doc.text('Servicio', margin + 4, y + 5);
    doc.text('N° de Sesión', pageWidth - margin - 30, y + 5);
    y += 10;

    // Table rows
    history.sessionServices.forEach(ss => {
      checkPageBreak(8);
      doc.setDrawColor(...colors.line);
      doc.setLineWidth(0.2);
      doc.line(margin, y - 1, pageWidth - margin, y - 1);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      doc.setTextColor(...colors.dark);
      const nameLines = doc.splitTextToSize(ss.serviceName, contentWidth - 50);
      doc.text(nameLines[0] || '-', margin + 4, y + 3.5);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...colors.primary);
      doc.text(ss.sessionNumber || '-', pageWidth - margin - 20, y + 3.5, {
        align: 'center'
      });
      y += 8;
    });
    y += 2;
  } else if (history.sessions || history.sessionNumber) {
    drawFieldPair('Sesiones:', history.sessions, 'N° de Sesión:', history.sessionNumber);
  } else {
    drawTextBlock('', '-');
  }

  // ========== COMENTARIOS ==========
  drawSectionTitle('Comentarios');
  drawTextBlock('', history.comments || '-');

  // ========== FIRMA - FORZAR AL FINAL DE LA PÁGINA ==========
  const signatureBlockHeight = 45;
  const signatureY = pageHeight - 25 - signatureBlockHeight;
  if (y < signatureY) {
    y = signatureY;
  }

  // Separator line before signature
  doc.setDrawColor(...colors.line);
  doc.setLineWidth(0.3);
  doc.line(margin, y, pageWidth - margin, y);
  y += 15;

  // Signature line
  const sigX = pageWidth / 2;
  doc.setDrawColor(...colors.dark);
  doc.setLineWidth(0.5);
  doc.line(sigX - 35, y, sigX + 35, y);
  y += 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...colors.dark);
  doc.text(history.doctorSignature || history.doctorName, sigX, y, {
    align: 'center'
  });
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...colors.gray);
  doc.text('Firma del Dr. (a)', sigX, y, {
    align: 'center'
  });
  y += 4;
  doc.setFontSize(7.5);
  doc.text(formatDateForPdf(history.date), sigX, y, {
    align: 'center'
  });

  // ========== ATTACHED IMAGES - EACH ON ITS OWN PAGE ==========
  if (attachedImages && attachedImages.length > 0) {
    for (let i = 0; i < attachedImages.length; i++) {
      const img = attachedImages[i];
      doc.addPage();

      // Page header
      doc.setFillColor(...colors.primary);
      doc.rect(0, 0, pageWidth, 3, 'F');
      let imgY = 10;

      // Title
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(...colors.primary);
      doc.text('IMAGEN ADJUNTA', pageWidth / 2, imgY + 6, {
        align: 'center'
      });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...colors.gray);
      doc.text(`Paciente: ${pb.fullName || patientName || '-'}  |  Fecha: ${formatDateForPdf(history.date)}  |  Imagen ${i + 1} de ${attachedImages.length}`, pageWidth / 2, imgY + 13, {
        align: 'center'
      });
      imgY += 20;

      // Divider
      doc.setDrawColor(...colors.primary);
      doc.setLineWidth(0.5);
      doc.line(margin, imgY, pageWidth - margin, imgY);
      imgY += 8;

      // Image name
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...colors.dark);
      doc.text(img.name || `Imagen ${i + 1}`, margin, imgY);
      imgY += 6;

      // Add the image - fit to page width while maintaining aspect ratio
      try {
        const imgMaxWidth = contentWidth;
        const imgMaxHeight = pageHeight - imgY - 30; // Leave space for footer

        // Detect image format from data URL
        let format = 'JPEG';
        if (img.data.includes('image/png')) format = 'PNG';else if (img.data.includes('image/gif')) format = 'PNG'; // jsPDF treats GIF as PNG

        doc.addImage(img.data, format, margin, imgY, imgMaxWidth, imgMaxHeight, undefined, 'FAST');
      } catch (imgErr) {
        logger.error(`Error adding image ${i + 1} to PDF:`, imgErr);
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(10);
        doc.setTextColor(200, 50, 50);
        doc.text('Error al cargar esta imagen', pageWidth / 2, imgY + 20, {
          align: 'center'
        });
      }
    }
  }

  // ========== FOOTER on all pages ==========
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    // Bottom accent bar
    doc.setFillColor(...colors.primary);
    doc.rect(0, pageHeight - 3, pageWidth, 3, 'F');
    // Page number
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...colors.lightGray);
    doc.text(`Página ${i} de ${totalPages}`, pageWidth / 2, pageHeight - 6, {
      align: 'center'
    });
    doc.text('Savidna - Historia Clínica Confidencial', margin, pageHeight - 6);
    doc.text(formatDateForPdf(history.date), pageWidth - margin, pageHeight - 6, {
      align: 'right'
    });
  }

  // Download
  const fileName = `Historia_Clinica_${(pb.fullName || patientName || 'paciente').replace(/\s+/g, '_')}_${new Date(history.date).toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
}